import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { API_BASE } from "@/lib/apiBase";

/*
 * Emergent Google OAuth callback handler.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. Redirect target is built from window.location.origin
 * on the Login page; this component just consumes the #session_id fragment.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginFromSession } = useAuth();
  const { t } = useT();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      setError("missing_session_id");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const r = await axios.post(
          `${API_BASE}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        loginFromSession(r.data);
        // Clean URL (remove fragment) and route to onboarding/dashboard.
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(r.data?.name ? `Google · ${r.data.name}` : (t("login.welcome") || "Welcome"));
        navigate("/onboarding", { replace: true, state: { user: r.data } });
      } catch (e) {
        setError(e?.response?.data?.detail || "auth_failed");
        toast.error("Login fehlgeschlagen. Bitte erneut versuchen.");
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen grid place-items-center bg-black"
      data-testid="auth-callback-page"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-neon/30 border-t-neon rounded-full animate-spin" />
        <p className="text-sm text-zinc-400" data-testid="auth-callback-status">
          {error ? "Auth-Fehler…" : "Sign-in wird abgeschlossen…"}
        </p>
      </div>
    </div>
  );
}
